import type { AssetDationItem } from '../../../../constants/payment';
import { Auth } from 'aws-amplify';
import appConfig from '../../../../config/app-config';

const { address: API_URL, key: API_KEY } = appConfig.apis.settlementV2;

const getHeaders = async () => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'x-api-key': API_KEY,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
};

// ─── Operações ──────────────────────────────────────────────────────────────

/** Uma operação como o settlement a vê: o do credit + o que os títulos dizem sobre ela. */
export interface SettlementOperation {
    credit_operation_id: string;
    sequencial_number: number;
    amendment_number?: number;
    contract_label?: string;
    operation_number?: string | null;
    operation_type: string;
    credit_product_type_name?: string;
    /** emitente/cedente já resolvido na view (ASSIGNOR/BORROWER preferencial) */
    issuer_name?: string | null;
    issuer_document?: string | null;
    issuer_type?: string | null;
    /** soma dos title_debt da operação — o que ela DEVE. NULL quando os títulos não chegaram. */
    amount?: number | null;
    paid_amount?: number | null;
    /** do credit; não responde pelo que a operação deve — ver AGENTS.md */
    nominal_value?: number | null;
    /** o DESEMBOLSADO (disbursement.totalAmount); não existe mais "valor aprovado" aqui */
    disbursement_value?: number | null;
    expiration_date?: string | null;
    business_reference?: string | null;
    title_debts_count?: number;
    status?: string;
}

/**
 * Busca de operações para as telas de cobrança.
 *
 * Vem do settlement e não do contracts_management de propósito: aqui a operação interessa pelo que
 * ela DEVE (`amount`, soma dos títulos) e pelo que já foi recebido (`paid_amount`), e o
 * contracts_management só conhece o `nominal_value` do credit.
 *
 * `status = 'Finalizado com sucesso'` e `billed IS TRUE` já são chumbados no servidor — não
 * precisa mandar como filtro fixo.
 */
export const listOperations = async (
    params: Record<string, string>,
): Promise<{ list: SettlementOperation[]; totalElements: number; totalPages: number }> => {
    // URLSearchParams codifica espaço como "+", e o API Gateway não converte de volta
    const qs = new URLSearchParams(params).toString().replace(/\+/g, '%20');
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/operations?${qs}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return {
        list: data.list || [],
        totalElements: data.totalElements ?? 0,
        totalPages: data.totalPages ?? 0,
    };
};

// ─── Title Debts ────────────────────────────────────────────────────────────

export const listTitleDebts = async (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts?${qs}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getTitleDebtDetail = async (id: number | string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${id}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getTitleDebtMovements = async (id: number | string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${id}/movements`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ─── Dossiê do título (GET /title-debts/{ref}/history) ──────────────────────
//
// O MESMO corpo dos eventos do dossiê (TITLE_DEBT_JUDICIAL_PETITION, _SETTLED...), sem o envelope:
// o título, o devedor, a operação e as movimentações, cada uma com a sua fatura, o pagamento e os
// eventos de cobrança. Fonte: settlement/docs/specs/title-debt-event.schema.json. Os tipos cobrem o
// que as telas leem; o corpo traz mais (endereço, dados brutos em `data`).

/** O DEVEDOR do título ou o PAGADOR de um pagamento — o dossiê usa o mesmo formato para os dois. */
export interface TitleDebtHistoryPerson {
    /** serial no devedor, uuid no pagador (assimetria conhecida do settlement) */
    person_id: number | string | null;
    external_id: string | null;
    name: string | null;
    document_number: string | null;
}

/** Ação de cobrança sobre a fatura: protesto, Serasa, mensagem. */
export interface TitleDebtHistoryBillingEvent {
    billing_event_id: string;
    billing_event_ref: string;
    billing_event_name: string | null;
    status: string;
    created_by: string | null;
    created_by_name: string | null;
    finished_by: string | null;
    finished_by_name: string | null;
    completed_at: string | null;
    event_date_limit: string | null;
    notes: string[] | null;
    created_at: string | null;
}

export interface TitleDebtHistoryPayment {
    payment_id: string;
    amount: number;
    payment_date: string | null;
    payment_method: string | null;
    payment_type: string | null;
    payer: TitleDebtHistoryPerson | null;
    receiving_bank_account: {
        bank_name: string | null;
        exibition_name: string | null;
        alias_name: string | null;
    } | null;
    user_responsible: string | null;
    notes: string | null;
}

export interface TitleDebtHistoryInvoice {
    invoice_id: string;
    amount: number;
    amount_paid: number | null;
    due_date: string | null;
    total_payment: boolean | null;
    paid_by: TitleDebtHistoryPayment | null;
    /** pode ser de OUTRO charge: pagar a garantia move as parcelas, e a fatura é a da garantia */
    charge_id: string | null;
    biller_our_number: string | null;
    status: string | null;
    payment_source: string | null;
    billing_events: TitleDebtHistoryBillingEvent[] | null;
}

export interface TitleDebtHistoryMovement {
    id: number;
    title_debt_id: string;
    invoice_id: string | null;
    movement_type: string;
    /** o DELTA do movimento; o acumulado é o `paid_amount` da raiz */
    amount: number;
    movement_date: string | null;
    notes: string | null;
    invoice: TitleDebtHistoryInvoice | null;
}

export interface TitleDebtHistory {
    title_debt_id: string;
    credit_operation_id: string | null;
    charge_id: string | null;
    credit_product_type_name: string | null;
    operation_type: string | null;
    operation_number: string | null;
    sequencial_number: number | null;
    installment_number: number | null;
    total_installments: number | null;
    /** valor de face do título */
    amount: number;
    paid_amount: number;
    due_date: string | null;
    status: string;
    amendment_number: number | null;
    business_reference: string | null;
    contract_number: string | null;
    document_title: string | null;
    subrogation: boolean;
    judicial_petition: boolean;
    debtor: TitleDebtHistoryPerson | null;
    days_overdue: number;
    movements_count: number;
    movements: TitleDebtHistoryMovement[];
}

/** Falha HTTP com o status: quem chama distingue "título não existe" (404) de cobrança fora do ar. */
export class SettlementHttpError extends Error {
    constructor(public status: number, message: string) {
        super(message);
    }
}

/**
 * O dossiê de um título. Quem lê de fora é o jurídico (recovery, Task 16 / AF-3694), que mostra o
 * que a cobrança fez com o título depois do envio sem guardar cópia.
 *
 * ⚠️ Só vêm as faturas que MOVIMENTARAM o título: o dossiê pendura cada fatura no seu movimento.
 * Fatura em aberto, e o protesto ou Serasa dela, não aparece aqui.
 */
export const getTitleDebtHistory = async (titleDebtRef: string): Promise<TitleDebtHistory> => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${encodeURIComponent(titleDebtRef)}/history`, { headers });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new SettlementHttpError(res.status, body?.message || `HTTP ${res.status}`);
    }
    return res.json();
};

/**
 * Histórico de movimentações de TODOS os títulos de uma operação (AF-3400).
 * Mesmo formato de getTitleDebtMovements; cada movimentação diz de qual título veio.
 * 404 quando a operação não tem título nenhum.
 */
export const getOperationMovements = async (creditOperationId: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/operations/${creditOperationId}/movements`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ─── Payments (Baixas) ──────────────────────────────────────────────────────

export interface RegisterPaymentPayload {
    amount: number;
    payment_date?: string;  // instante ISO (UTC) da baixa
    payment_type: string;
    payment_method?: string;
    settlement_type: string;
    bank_account?: string;                 // alias (compat/exibição)
    receiving_bank_account_id?: string;    // conta de recebimento (alias ou uuid) — canônico
    payment_source?: string;
    pending_guarantor_refund?: boolean;
    notes?: string;
    debt_confession_contract?: string;
    /** UM bem, não uma lista — ver `paymentPayload` e o schema do billing */
    asset_dation?: AssetDationItem | null;
    /** pagador informado na tela — sobrepõe o payer do título quando presente */
    payer?: {
        external_id?: string;
        name: string;
        document_number: string;
        email?: string;
        phone?: string;
        address_street?: string;
        address_number?: string;
        address_complement?: string;
        address_neighborhood?: string;
        address_city?: string;
        address_state?: string;
        address_zip_code?: string;
    };
    file_name_receipt?: string;
    file_url_receipt?: string;
    user_responsible?: string;
}

export const registerPayment = async (titleDebtId: number | string, payload: RegisterPaymentPayload) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${titleDebtId}/payments`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        const message = errorBody?.message || `Erro HTTP ${res.status}`;
        throw new Error(message);
    }
    return res.json();
};

// TODO: Endpoint batch-payments (AF-2729 Fase 3)
export interface BatchPaymentItem {
    title_debt_id: number;
    amount: number;
    payment_method: string;
    settlement_type: string;
    bank_account?: string;
    receiving_bank_account_id?: string;
    payment_source?: string;
    notes?: string;
}

export const registerBatchPayments = async (payments: BatchPaymentItem[]) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/batch-payments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ payments }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ─── Reversals ──────────────────────────────────────────────────────────────

export interface ReversalPayload {
    reasons: string[];
    notes?: string;
}

export const registerReversal = async (titleDebtId: number | string, payload: ReversalPayload) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/title-debts/${titleDebtId}/reversals`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        const message = errorBody?.message || `Erro HTTP ${res.status}`;
        throw new Error(message);
    }
    return res.json();
};

// ─── Invoices ───────────────────────────────────────────────────────────────

export const listInvoices = async (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/invoices?${qs}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

// ─── Simulations (TODO: settlement-simulation-api) ──────────────────────────

export interface PaymentSimulationPayload {
    contract_operation_id: string;
    amount: number;
    payment_date: string;
    payment_source?: string;
}

export const simulatePayment = async (payload: PaymentSimulationPayload) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/simulations/payment`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getMaturityBuckets = async (contractOperationId?: string) => {
    const headers = await getHeaders();
    const qs = contractOperationId ? `?contract_operation_id=${contractOperationId}` : '';
    const res = await fetch(`${API_URL}/maturities/buckets${qs}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getMaturityProjections = async (contractOperationId: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/maturities/projections?contract_operation_id=${contractOperationId}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const simulateEarlyPayment = async (titleDebtId: number | string, paymentDate: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/simulations/early-payment`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ title_debt_id: titleDebtId, payment_date: paymentDate }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const getFinancialMetrics = async (contractOperationId: string) => {
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/financials/${contractOperationId}`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};
